// ============================================================
// REACT & LIBRARIES
// ============================================================
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DataTable from "react-data-table-component";

// ============================================================
// COMMON COMPONENTS
// ============================================================
import FilterComponent from "../../Common/FilterComponent";
import { H5, P } from "../../../../AbstractElements";

// ============================================================
// DATA TABLE
// ============================================================
import { EmployeeTableColumn } from "../../../../Data/DataTables/Employees";

// ============================================================
// TYPES
// ============================================================
import { ViewEmployee } from "../../../../Types/Workforce/201File";
import { CRUDPermissions } from "../../../../Types/Common/Access";
import { PaginatedResponse } from "../../../../Types/Common/PaginatedResponse";

// ============================================================
// SERVICES
// ============================================================
import {
  VIEW_EMPLOYEE_QUERY_KEY,
  Employee201FileService,
} from "../../../../Services/201fileServices";

// ============================================================
// PROPS
// ============================================================

interface UserAccessProps {
  access: CRUDPermissions;
}

// ============================================================
// COMPONENT
// ============================================================

const Employee201Table: React.FC<UserAccessProps> = ({ access }) => {
  // ----------------------------------------------------------
  // STATE
  // ----------------------------------------------------------

  // Search input
  const [filterText, setFilterText] = useState("");

  // Server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // ----------------------------------------------------------
  // REACT QUERY
  // ----------------------------------------------------------

  const {
    data: employeeResponse,
    isLoading: isFetching,
    isError,
  } = useQuery<PaginatedResponse<ViewEmployee>>({
    queryKey: [...VIEW_EMPLOYEE_QUERY_KEY, currentPage, perPage, filterText],

    queryFn: () =>
      Employee201FileService.getAllEmployee(currentPage, perPage, filterText),

    placeholderData: (previousData) => previousData,
  });

  const employeeData: ViewEmployee[] = [...(employeeResponse?.data ?? [])].sort(
    (a, b) => Number(a.employment_status) - Number(b.employment_status),
  );

  // ----------------------------------------------------------
  // DATATABLE STYLES
  // ----------------------------------------------------------

  const customStyles = {
    rows: {
      style: {
        whiteSpace: "nowrap" as const,
      },
    },
    cells: {
      style: {
        whiteSpace: "nowrap" as const,
        marginTop: "8px",
        marginBottom: "8px",
      },
    },
  };

  // ----------------------------------------------------------
  // ACCESS CONTROL
  // ----------------------------------------------------------

  if (!access.read) {
    return (
      <P className="text-danger">
        You do not have permission to view employee records.
      </P>
    );
  }

  // ----------------------------------------------------------
  // CONTENT
  // ----------------------------------------------------------

  return (
    <>
      <H5 className="mb-0">Employee 201 File</H5>

      <P className="mb-3 f-light f-w-200 f-12">
        Displays all employee 201 files containing complete personnel records,
        employment information, documents, and historical data for employee
        reference, monitoring, and HR management purposes.
      </P>

      <FilterComponent
        filterText={filterText}
        onFilter={(e: React.ChangeEvent<HTMLInputElement>) => {
          setFilterText(e.target.value);

          setCurrentPage(1);
        }}
      />

      <div className="table-responsive theme-scrollbar">
        <DataTable
          className="display nowrap custom-scrollbar"
          columns={EmployeeTableColumn(access)}
          data={employeeData}
          customStyles={customStyles}
          striped
          highlightOnHover
          fixedHeader
          pagination
          paginationServer
          paginationTotalRows={employeeResponse?.total ?? 0}
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onChangePage={(page) => setCurrentPage(page)}
          onChangeRowsPerPage={(newPerPage) => {
            setPerPage(newPerPage);
            setCurrentPage(1);
          }}
          progressPending={isFetching}
          noDataComponent={
            isError
              ? "Unable to retrieve employee records."
              : "No employee records found."
          }
        />
      </div>
    </>
  );
};

export default Employee201Table;
